"use client";

import { type ReactNode, type Ref, useId } from "react";
import {
  linearScale,
  niceStep,
  tickDecimals,
  ticksBetween,
  type Viewport,
} from "@/lib/tool-kit/math/plot";
import { CHART_INK } from "@/lib/tool-kit/palette";

/**
 * The coordinate plane the math family draws its graphs on: grid, axes, tick labels,
 * and a clipped plotting area handed to the caller as two scale functions.
 *
 * Every colour and font is an attribute, not a class, so the same SVG downloads as a
 * PNG that looks like the one on the page. All visible text comes in through props.
 */

export const PLANE_WIDTH = 480;
export const PLANE_HEIGHT = 360;
const MARGIN = { left: 48, right: 16, top: 16, bottom: 36 };
export const PLOT_WIDTH = PLANE_WIDTH - MARGIN.left - MARGIN.right;
export const PLOT_HEIGHT = PLANE_HEIGHT - MARGIN.top - MARGIN.bottom;
export const PLANE_FONT = "system-ui, -apple-system, Segoe UI, sans-serif";

const UNSAFE_ID = /[^a-zA-Z0-9_-]/g;

export interface PlaneScales {
  x: (value: number) => number;
  y: (value: number) => number;
  viewport: Viewport;
}

export function CoordinatePlane({
  viewport,
  title,
  formatTick,
  svgRef,
  children,
}: {
  viewport: Viewport;
  /** The graph's accessible name — what it shows, in a sentence. */
  title: string;
  formatTick: (value: number, decimals: number) => string;
  svgRef?: Ref<SVGSVGElement>;
  children: (scales: PlaneScales) => ReactNode;
}) {
  const uid = useId().replace(UNSAFE_ID, "");
  const clipId = `plane-clip-${uid}`;
  const titleId = `plane-title-${uid}`;
  const left = MARGIN.left;
  const right = PLANE_WIDTH - MARGIN.right;
  const top = MARGIN.top;
  const bottom = PLANE_HEIGHT - MARGIN.bottom;
  const x = linearScale(viewport.xMin, viewport.xMax, left, right);
  const y = linearScale(viewport.yMin, viewport.yMax, bottom, top);
  const xStep = niceStep(viewport.xMax - viewport.xMin, 8);
  const yStep = niceStep(viewport.yMax - viewport.yMin, 6);
  const xTicks = ticksBetween(viewport.xMin, viewport.xMax, xStep);
  const yTicks = ticksBetween(viewport.yMin, viewport.yMax, yStep);
  const xAxisAt = viewport.yMin <= 0 && viewport.yMax >= 0 ? y(0) : null;
  const yAxisAt = viewport.xMin <= 0 && viewport.xMax >= 0 ? x(0) : null;

  return (
    <svg
      aria-labelledby={titleId}
      className="h-auto w-full max-w-xl"
      fontFamily={PLANE_FONT}
      ref={svgRef}
      role="img"
      viewBox={`0 0 ${PLANE_WIDTH} ${PLANE_HEIGHT}`}
      xmlns="http://www.w3.org/2000/svg"
    >
      <title id={titleId}>{title}</title>
      <rect
        fill={CHART_INK.background}
        height={PLANE_HEIGHT}
        width={PLANE_WIDTH}
      />
      <defs>
        <clipPath id={clipId}>
          <rect height={bottom - top} width={right - left} x={left} y={top} />
        </clipPath>
      </defs>
      {xTicks.map((tick) => (
        <g key={`x${tick}`}>
          <line
            stroke={CHART_INK.grid}
            x1={x(tick)}
            x2={x(tick)}
            y1={top}
            y2={bottom}
          />
          <text
            fill={CHART_INK.muted}
            fontSize={11}
            textAnchor="middle"
            x={x(tick)}
            y={bottom + 16}
          >
            {formatTick(tick, tickDecimals(xStep))}
          </text>
        </g>
      ))}
      {yTicks.map((tick) => (
        <g key={`y${tick}`}>
          <line
            stroke={CHART_INK.grid}
            x1={left}
            x2={right}
            y1={y(tick)}
            y2={y(tick)}
          />
          <text
            dominantBaseline="middle"
            fill={CHART_INK.muted}
            fontSize={11}
            textAnchor="end"
            x={left - 6}
            y={y(tick)}
          >
            {formatTick(tick, tickDecimals(yStep))}
          </text>
        </g>
      ))}
      <rect
        fill="none"
        height={bottom - top}
        stroke={CHART_INK.axis}
        width={right - left}
        x={left}
        y={top}
      />
      {xAxisAt === null ? null : (
        <line
          stroke={CHART_INK.text}
          strokeWidth={1.25}
          x1={left}
          x2={right}
          y1={xAxisAt}
          y2={xAxisAt}
        />
      )}
      {yAxisAt === null ? null : (
        <line
          stroke={CHART_INK.text}
          strokeWidth={1.25}
          x1={yAxisAt}
          x2={yAxisAt}
          y1={top}
          y2={bottom}
        />
      )}
      <text
        fill={CHART_INK.text}
        fontSize={12}
        fontStyle="italic"
        textAnchor="end"
        x={right}
        y={bottom + 30}
      >
        x
      </text>
      <text
        fill={CHART_INK.text}
        fontSize={12}
        fontStyle="italic"
        x={6}
        y={top + 10}
      >
        y
      </text>
      <g clipPath={`url(#${clipId})`}>{children({ x, y, viewport })}</g>
    </svg>
  );
}

/** A marked point with a label beside it, kept inside the frame. */
export function PlanePoint({
  cx,
  cy,
  color,
  label,
  below = false,
}: {
  cx: number;
  cy: number;
  color: string;
  label?: string;
  /** Put the label under the point — for a minimum, where the curve rises away. */
  below?: boolean;
}) {
  const nearRight = cx > PLANE_WIDTH - 140;
  const nearTop = cy < MARGIN.top + 24;
  const nearBottom = cy > PLANE_HEIGHT - MARGIN.bottom - 24;
  const underneath = (below && !nearBottom) || nearTop;
  return (
    <g>
      <circle
        cx={cx}
        cy={cy}
        fill={color}
        r={4.5}
        stroke={CHART_INK.background}
        strokeWidth={1.5}
      />
      {label ? (
        <text
          fill={CHART_INK.text}
          fontSize={12}
          textAnchor={nearRight ? "end" : "start"}
          x={nearRight ? cx - 8 : cx + 8}
          y={underneath ? cy + 20 : cy - 8}
        >
          {label}
        </text>
      ) : null}
    </g>
  );
}
